import { useState, useCallback } from 'react';
import { authApi } from '../api/authApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

/**
 * Hook personalizado para gestión de autenticación
 * Proporciona estado y funciones para login, logout y verificación de autenticación
 */
export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Inicia sesión con username y password
   */
  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authApi.login(username, password);
      showSuccessToast('Sesión iniciada exitosamente');
      return response;
    } catch (err) {
      // No mostrar toast aquí, se mostrará en el componente Login
      // para tener mejor control del mensaje
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          err.message || 
                          'Error al iniciar sesión';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cierra la sesión del usuario
   */
  const logout = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      await authApi.logout();
      showSuccessToast('Sesión cerrada exitosamente');
      return true;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cerrar sesión');
      setError(errorMessage);
      // No lanzar error porque el logout local ya se hizo
      return true;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verifica si el usuario está autenticado
   */
  const isAuthenticated = useCallback(() => {
    return authApi.isAuthenticated();
  }, []);

  /**
   * Obtiene el token actual
   */
  const getToken = useCallback(() => {
    return authApi.getToken();
  }, []);

  /**
   * Obtiene el usuario actual desde localStorage
   */
  const getCurrentUser = useCallback(() => {
    return authApi.getCurrentUser();
  }, []);

  /**
   * Obtiene el perfil del usuario desde el servidor
   */
  const getMe = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const user = await authApi.me();
      return user;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al obtener perfil del usuario');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Renueva el token de autenticación
   */
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authApi.refresh();
      return response;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al renovar token');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // Estado
    loading,
    error,
    
    // Funciones
    login,
    logout,
    isAuthenticated,
    getToken,
    getCurrentUser,
    getMe,
    refresh,
    getAllProjects: authApi.getAllProjects,
    getUsuariosProyectos: authApi.getUsuariosProyectos,
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useAuth;
